import React from "react";
import { SupText } from "@repo/core-ui";
import { HeroStats } from "@/types";
import CountUp from "react-countup";

interface CredInfoPanelProps {
  perHourTotal?: HeroStats["cred"]["per_hour_total"];
  perHourDeposits?: HeroStats["cred"]["per_hour_deposits"];
  perHourReferralsDirect?: HeroStats["cred"]["per_hour_referrals_direct"];
  perHourReferralsIndirect?: HeroStats["cred"]["per_hour_referrals_indirect"];
  referralsDirect?: HeroStats["referrals_direct_count"];
  referralsIndirect?: HeroStats["referrals_indirect_count"];
}

interface FlowRowProps {
  value?: number;
  label: string;
  isHeader?: boolean;
  isLast?: boolean;
  subLabel?: string;
  totalCount?: number;
}

const formatNumber = (num?: number) => {
  if (!num) return 0;
  if (num >= 1000) {
    return Number((num / 1000).toFixed(1));
  }
  return Math.round(num);
};

const FlowRow = ({ value, label, isHeader, isLast, subLabel, totalCount }: FlowRowProps) => (
  <>
    <div
      className={`flex ${isHeader ? "h-[112px] items-center justify-start pl-4" : "items-center justify-end space-x-2 pr-4"} ${
        !isLast ? "border-b-0.5" : ""
      } border-r-0.5 border-primary-400 ${isHeader ? "bg-primary-100/90" : "bg-neutral-50"}`}
    >
      {isHeader ? (
        <div className="flex flex-col">
          <SupText className="text-[32px] font-bold leading-none text-primary-700" family="mono" variant="body1" weight="regular">
            <CountUp end={formatNumber(value ?? 0)} decimals={value && value >= 1000 ? 1 : 0} prefix={!isHeader ? "+" : ""} />
            {value && value > 1000 ? "K" : null}
          </SupText>
          <SupText className="text-[14px] font-bold text-primary-700" family="mono" variant="body1" weight="regular">
            CRED / HR
          </SupText>
        </div>
      ) : (
        <>
          <SupText className="pl-2" family="mono" variant="body1" weight="regular">
            <CountUp end={formatNumber(value ?? 0)} decimals={value && value >= 1000 ? 1 : 0} prefix={!isHeader ? "+" : ""} />
            {value && value > 1000 ? "K" : null}
          </SupText>
          <SupText className="text-primary-600" family="mono" variant="body2" weight="regular">
            CRED / HR
          </SupText>
        </>
      )}
    </div>
    <div
      className={`flex ${subLabel ? "flex-col" : ""} ${isHeader ? "h-[112px] items-center" : "items-end"} justify-end ${!isLast ? "border-b-0.5" : ""} border-primary-400 ${
        isHeader ? "bg-neutral-50" : ""
      } px-4 ${subLabel ? "py-2.5" : "py-4"}`}
    >
      <SupText
        className={`${isHeader ? "text-lg" : "text-[14px]"} uppercase text-neutral-${isHeader ? "950" : "900"}`}
        family="mono"
        variant="body1"
        weight={isHeader ? "bold" : "regular"}
      >
        {label}
      </SupText>
      {subLabel && (
        <SupText className="uppercase text-neutral-800" family="mono" variant="body2" weight="regular">
          <CountUp end={totalCount ?? 0} suffix=" total" />
        </SupText>
      )}
    </div>
  </>
);

export default function CredFlowInfoPanel({
  perHourTotal,
  perHourDeposits,
  perHourReferralsDirect,
  perHourReferralsIndirect,
  referralsDirect,
  referralsIndirect,
}: CredInfoPanelProps) {
  const flowData = [
    {
      value: perHourTotal,
      label: "CRED FLOW",
      isHeader: true,
    },
    {
      value: perHourDeposits,
      label: "Deposits",
    },
    {
      value: perHourReferralsDirect,
      label: "DIRECT REFERRALS",
      subLabel: "total",
      totalCount: referralsDirect,
    },
    {
      value: perHourReferralsIndirect,
      label: "INDIRECT REFERRALS",
      subLabel: "total",
      totalCount: referralsIndirect,
      isLast: true,
    },
  ];

  return (
    <div>
      <div className="grid w-full grid-cols-[1fr_2fr] grid-rows-[minmax(112px,_auto)_3fr] overflow-hidden rounded-3xl rounded-tr-none border-0.5 border-primary-600">
        {flowData.map(flow => (
          <FlowRow key={flow.label} {...flow} />
        ))}
      </div>
    </div>
  );
}
